import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';

export type EmptyStateVariant = 'zero' | 'inline';

export interface EmptyStateProps extends Omit<ComponentProps<'div'>, 'title'> {
  /**
   * `zero` — the full-page treatment: a 52px coral tile, a display title, one
   * short line, the action that fills the emptiness.
   * `inline` — what a list says when it holds nothing: a bare muted glyph, a
   * smaller title, no tile. The product uses this one *inside* a page whose
   * header and filters are still there, where a coral tile would read as an
   * announcement rather than as an absence.
   */
  variant?: EmptyStateVariant;
  /** In `zero`, sits in a 52px coral tile above the title; in `inline`, bare. */
  icon?: ReactNode;
  title: ReactNode;
  /**
   * Attributes for the title and the explanatory line.
   *
   * They exist so a screen can put a `data-fidelity` marker on the two
   * elements the measurements name, without this component knowing what a
   * measurement is (`e2e/tests/fidelity.spec.ts`).
   *
   * `data-fidelity` is spelled out rather than left to the `data-*` escape
   * hatch: that one works in JSX position and not in an object typed against
   * the element, which is what these are.
   */
  titleProps?: ComponentProps<'h3'> & { 'data-fidelity'?: string };
  description?: ReactNode;
  descriptionProps?: ComponentProps<'p'> & { 'data-fidelity'?: string };
  /** Buttons, laid out in a centred row that wraps. */
  actions?: ReactNode;
  /** A last quiet line under the actions — a price, a caveat. */
  footnote?: ReactNode;
}

/**
 * What a list says when it holds nothing yet.
 *
 * The product treats this as a real screen rather than as an apology: a coral
 * icon tile, a display-face title, one line of explanation held to 46
 * characters so it never becomes a paragraph, and the action that fills the
 * emptiness.
 */
export function EmptyState({
  variant = 'zero',
  icon,
  title,
  titleProps,
  description,
  descriptionProps,
  actions,
  footnote,
  className,
  ...props
}: EmptyStateProps) {
  const inline = variant === 'inline';

  return (
    <div
      className={cn(
        'flex flex-col items-center text-center',
        // Measured: the product's `.zero` is 430px wide and padded 56/24, and
        // the width is what keeps the one explanatory line one line.
        inline ? 'gap-5 px-6 py-6' : 'mx-auto max-w-[430px] px-6 py-14',
        className,
      )}
      {...props}
    >
      {icon ? (
        inline ? (
          <span className="flex text-ink-muted [&>svg]:size-6">{icon}</span>
        ) : (
          // 15px, not the 16px card radius, and 18px of air under it. Both
          // measured off `.zicon`; both are the kind of near-miss that makes a
          // screen feel almost right.
          <span className="mb-[18px] flex size-[52px] items-center justify-center rounded-[15px] bg-brand-200 text-brand-600 [&>svg]:size-[26px]">
            {icon}
          </span>
        )
      ) : null}
      <h3
        {...titleProps}
        className={cn(
          'text-ink',
          inline
            ? // Measured: the display face here too, at 20/24, and tracking
              // *normal* — unlike the page-sized title, which is tightened.
              'font-display text-[20px] leading-6 font-bold tracking-normal'
            : 'font-display text-lg font-bold tracking-tight',
        )}
      >
        {title}
      </h3>
      {description ? (
        <p
          {...descriptionProps}
          className={cn(
            'text-ink-muted',
            inline ? 'text-bodysm' : 'mt-2 text-bodysm leading-relaxed',
          )}
        >
          {description}
        </p>
      ) : null}
      {actions ? (
        <div className={cn('flex flex-wrap justify-center gap-2.5', inline ? '' : 'mt-4')}>
          {actions}
        </div>
      ) : null}
      {footnote ? (
        <p className={cn('text-sm text-ink-faint', inline ? '' : 'mt-4')}>{footnote}</p>
      ) : null}
    </div>
  );
}
